import { Form, usePage } from "@inertiajs/react"
import { ChevronRight } from "lucide-react"
import { useRef, useState } from "react"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { userPath } from "@/routes"

export default function DeleteUser() {
  const { auth } = usePage().props
  const passwordInput = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="space-y-6">
      <div className="flex items-center justify-between">
        <HeadingSmall
          title="Delete account"
          description="Delete your account and all of its resources"
        />
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <ChevronRight
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                open && "rotate-90",
              )}
            />
            <span className="sr-only">Toggle delete section</span>
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="space-y-4 rounded-lg border border-red-100 bg-red-50 p-4 dark:border-red-200/10 dark:bg-red-700/10">
          <div className="relative space-y-0.5 text-red-600 dark:text-red-100">
            <p className="font-medium">Warning</p>
            <p className="text-sm">
              Please proceed with caution, this cannot be undone.
            </p>
          </div>

          <Dialog>
            <DialogTrigger asChild>
              <Button variant="destructive">Delete account</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogTitle>
                Are you sure you want to delete your account?
              </DialogTitle>
              <DialogDescription>
                Once your account is deleted, all of its resources and data will
                also be permanently deleted. Please enter your password to
                confirm you would like to permanently delete your account.
              </DialogDescription>
              <Form
                method="delete"
                action={userPath(auth.user.id)}
                options={{
                  preserveScroll: true,
                }}
                onError={() => passwordInput.current?.focus()}
                resetOnSuccess
                className="space-y-6"
              >
                {({ resetAndClearErrors, processing, errors }) => (
                  <>
                    <div className="grid gap-2">
                      <Label htmlFor="password_challenge" className="sr-only">
                        Password
                      </Label>

                      <Input
                        id="password_challenge"
                        type="password"
                        name="password_challenge"
                        ref={passwordInput}
                        placeholder="Password"
                        autoComplete="current-password"
                      />

                      <InputError messages={errors.password_challenge} />
                    </div>

                    <DialogFooter>
                      <DialogClose asChild>
                        <Button
                          variant="secondary"
                          onClick={() => resetAndClearErrors()}
                        >
                          Cancel
                        </Button>
                      </DialogClose>

                      <Button
                        variant="destructive"
                        disabled={processing}
                        asChild
                      >
                        <button type="submit">Delete account</button>
                      </Button>
                    </DialogFooter>
                  </>
                )}
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
